import React from 'react';
import styled, { css } from 'styled-components';

import { Check } from '@rover/icons';
import { Box, Flex } from '@rover/kibble/core';
import { Props as BoxProps } from '@rover/kibble/core/Box';
import { Color } from '@rover/kibble/styles';

type UnorderedListVariant = 'primary' | 'secondary';

export type UnorderedListVariantMap<T> = {
  [P in UnorderedListVariant]: T;
};

export const UnorderedListColorMap: UnorderedListVariantMap<Color> = {
  primary: Color.BRAND_ROVER,
  secondary: Color.NEUTRAL_BLACK,
};

export type Props = BoxProps & {
  children: React.ReactNode;
  centered?: boolean;
  icon?: Icons;
  listVariant?: UnorderedListVariant;
};

type IconListItemProps = {
  children: React.ReactNode;
  icon: Icons;
};

export enum Icons {
  CheckMark,
}

const iconResources = {
  [Icons.CheckMark]: (
    <Check
      data-qa-id="unordered-list-check"
      width="30px"
      height="30px"
      viewBox="4 4 24 24"
      fill={Color.GREEN_400}
    />
  ),
};
interface ListItemProps extends React.ComponentProps<typeof Box> {
  centered?: boolean;
  listVariant?: keyof typeof UnorderedListColorMap;
  children: React.ReactNode;
}

export const ListItem: React.FC<ListItemProps> = ({
  centered = false,
  listVariant = 'primary',
  children,
  ...props
}) => {
  return (
    <Box
      sx={{
        '&::marker': {
          color: UnorderedListColorMap[listVariant].toString(),
        },
        ...(centered && { listStylePosition: 'inside' }),
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

const StyledUnorderedList = styled(Box)<{ icon?: Icons }>`
  ${({ icon }) => {
    return (
      icon !== undefined &&
      css`
        padding: 0;
        padding-inline-start: 0;
      `
    );
  }}
  ${({ listVariant }) => {
    return (
      listVariant === 'secondary' &&
      css`
        padding: 0;
        padding-inline-start: 25px;
      `
    );
  }}
`;

const IconListItem = ({ icon, children }: IconListItemProps): JSX.Element => (
  <Flex as="li" mb="2x" flexDirection="row">
    <Flex justifyContent="center" minWidth="30px">
      {iconResources[icon]}
    </Flex>
    <ListItem ml="6x">{children}</ListItem>
  </Flex>
);

const UnorderedList = ({ centered = false, ...props }: Props): JSX.Element => {
  return (
    <StyledUnorderedList
      as="ul"
      textAlign={centered ? 'center' : undefined}
      pl={centered ? '0x' : undefined}
      {...props}
    >
      {React.Children.map(props.children, (item) =>
        props.icon === undefined ? (
          <ListItem as="li" mb="2x" centered={centered} listVariant={props.listVariant}>
            {item}
          </ListItem>
        ) : (
          <IconListItem icon={props.icon}>{item}</IconListItem>
        )
      )}
    </StyledUnorderedList>
  );
};

export default UnorderedList;
